import React from 'react';
import { useTheme } from 'styled-components';
import { cn } from '../../../../utils/tailwind';
import { createStableClassToken } from '../../../../utils/stable-class-token';
import { ButtonIcon } from './icon/button-icon';
import { IconSpinner } from '../../icons/spinner/spinner';
import { GapSizeType } from '../../../../theme/type/gap-type';
import { ButtonSizeType, ButtonVariantType, ButtonVariationType } from '../../../../theme/type/button-type';
import { BreakpointSizeType } from '../../../../theme/type/breakpoints-type';

export interface ButtonControlPropsInterface {
    children?: JSX.Element | Array<JSX.Element> | string;
    href?: string;
    as?: string;
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    loading?: boolean;
    id?: string;
    form?: string;
    button?: string;
    variation?: ButtonVariationType;
    size?: ButtonSizeType | object;
    variant?: ButtonVariantType;
    appearance?: 'classic' | 'gradient' | 'secondary-outlined' | 'neutral-outlined';
    block?: boolean;
    disabled?: boolean;
    icon?: React.ReactNode;
    gradient?: boolean;
    shadow?: boolean;
    hideLabelUnder?: BreakpointSizeType;
    hideLabelOver?: BreakpointSizeType;
    iconPosition?: 'left' | 'right';
    iconGap?: GapSizeType;
    compactIconSpacing?: boolean;
    disableResponsive?: boolean;
    type?: 'button' | 'submit' | 'reset' | undefined;
}

export const ButtonControl: React.FC<ButtonControlPropsInterface> = ({
    children,
    loading,
    variant = 'default',
    appearance = 'classic',
    size = 'medium',
    variation = 'filled',
    disabled,
    form,
    icon,
    iconPosition,
    iconGap = 'medium',
    compactIconSpacing = false,
    id,
    onClick,
    gradient,
    shadow,
    block,
    type,
    hideLabelUnder,
    hideLabelOver,
    disableResponsive = false,
}) => {
    const theme = useTheme();
    const classToken = createStableClassToken('button-control', [
        variant,
        appearance,
        variation,
        size,
        block,
        gradient,
        shadow,
        hideLabelUnder,
        hideLabelOver,
        disableResponsive,
    ]);
    const labelToken = createStableClassToken('button-label', [size, hideLabelUnder, hideLabelOver]);
    const themeStates = theme.button.variant[variant][variation];
    const secondaryColor = theme.palette.color.secondary.main;
    const isNeutral = appearance === 'neutral-outlined';
    const outlineColor = isNeutral ? '#d9cdc5' : secondaryColor;
    const neutralTextColor = '#72645a';
    const hoverColor = isNeutral ? 'rgba(217, 205, 197, 0.4)' : outlineColor;
    const filledTextColor = isNeutral ? neutralTextColor : '#ffffff';
    const states =
        appearance === 'secondary-outlined' || isNeutral
            ? {
                  ...themeStates,
                  base: {
                      ...themeStates.base,
                      background: 'transparent',
                      border: outlineColor,
                      color: isNeutral ? neutralTextColor : secondaryColor,
                  },
                  hover: {
                      ...themeStates.hover,
                      background: hoverColor,
                      border: hoverColor,
                      color: filledTextColor,
                      icon: { ...themeStates.hover.icon, color: filledTextColor },
                  },
                  active: {
                      ...themeStates.active,
                      background: hoverColor,
                      border: hoverColor,
                      color: filledTextColor,
                      icon: { ...themeStates.active.icon, color: filledTextColor },
                  },
              }
            : themeStates;
    const getPadding = (padding: string) =>
        appearance === 'gradient' && variation === 'filled'
            ? padding
                  .split(' ')
                  .map((value) => `calc(${value} + 1px)`)
                  .join(' ')
            : padding;

    const buttonStyle: React.CSSProperties = {
        borderRadius: '20rem',
        backgroundClip: isNeutral ? 'padding-box' : undefined,
        fontFamily: theme.base.fontFace,
        fontWeight: 700,
        justifyContent: icon ? 'space-between' : 'center',
        width: block ? '100%' : undefined,
        transition: `all ${theme.base.transition}`,
        boxShadow: shadow && variation === 'filled' ? states.base.shadow : undefined,
    };

    if (gradient && variation === 'filled') {
        buttonStyle.backgroundImage = 'linear-gradient(to left, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0))';
        buttonStyle.borderColor = 'transparent';
        buttonStyle.boxShadow = 'inset 0px 1px 1px rgba(255, 255, 255, 0.25)';
    }

    if (typeof size === 'string') {
        buttonStyle.padding = getPadding(theme.button.size[size].padding);
    }

    if (appearance === 'gradient' && variation === 'filled') {
        buttonStyle.backgroundColor = theme.palette.color.primary.main;
        buttonStyle.backgroundImage = 'linear-gradient(45deg, rgba(0, 0, 0, 0.5) 15%, rgba(255, 255, 255, 0.4) 85%)';
        buttonStyle.backgroundBlendMode = 'overlay';
        buttonStyle.border = '0';
        buttonStyle.boxShadow = 'none';
    }

    const responsivePaddingCss =
        typeof size === 'object'
            ? (Object.entries(size) as [BreakpointSizeType, ButtonSizeType][])
                  .map(([breakpoint, buttonSize], index) => {
                      const padding = getPadding(theme.button.size[buttonSize].padding);
                      const minQuery = theme.breakpoints[breakpoint].min;
                      const maxQuery = theme.breakpoints[breakpoint].max;

                      const chunk = (query: string) => `
                        @media ${query} {
                            .${classToken} {
                                padding: ${padding};
                            }
                        }
                    `;

                      if (index === 0) {
                          return `${chunk(maxQuery)} ${chunk(minQuery)}`;
                      }

                      return chunk(minQuery);
                  })
                  .join('\n')
            : '';

    const labelStyle: React.CSSProperties = {};
    if (typeof size === 'string') {
        labelStyle.fontSize = theme.button.size[size].fontSize;
        labelStyle.lineHeight = theme.button.size[size].lineHeight;
        // Keep individual sides explicit so moving an icon cannot clear shorthand padding during updates.
        const [verticalPadding, horizontalPadding = verticalPadding] = theme.button.size[size].labelPadding.split(' ');
        labelStyle.paddingTop = verticalPadding;
        labelStyle.paddingBottom = verticalPadding;
        labelStyle.paddingLeft = horizontalPadding;
        labelStyle.paddingRight = horizontalPadding;
        labelStyle.height = theme.button.size[size].icon.wrapper;
    }

    const responsiveLabelCss =
        typeof size === 'object'
            ? (Object.entries(size) as [BreakpointSizeType, ButtonSizeType][])
                  .map(([breakpoint, buttonSize], index) => {
                      const { fontSize } = theme.button.size[buttonSize];
                      const { lineHeight } = theme.button.size[buttonSize];
                      const padding = theme.button.size[buttonSize].labelPadding;
                      const height = theme.button.size[buttonSize].icon.wrapper;
                      const minQuery = theme.breakpoints[breakpoint].min;
                      const maxQuery = theme.breakpoints[breakpoint].max;

                      const chunk = (query: string) => `
                        @media ${query} {
                            .${labelToken} {
                                font-size: ${fontSize};
                                line-height: ${lineHeight};
                                padding: ${padding};
                                height: ${height};
                            }
                        }
                    `;

                      if (index === 0) {
                          return `${chunk(maxQuery)} ${chunk(minQuery)}`;
                      }

                      return chunk(minQuery);
                  })
                  .join('\n')
            : '';

    if (compactIconSpacing && icon && children) {
        if (iconPosition === 'left') {
            labelStyle.paddingLeft = '0.8rem';
        } else {
            labelStyle.paddingRight = '0.8rem';
        }
    }

    const styleElement = (
        <style>{`
                .${classToken} {
                    background-color: ${states.base.background};
                    border: 0.1rem solid ${states.base.border};
                    color: ${states.base.color};
                }

                .${classToken}:hover {
                    background-color: ${states.hover.background};
                    border-color: ${states.hover.border};
                    color: ${states.hover.color};
                }

                .${classToken}:hover [data-button-icon] {
                    background-color: ${states.hover.icon.background};
                    color: ${states.hover.icon.color};
                }

                .${classToken}:hover [data-button-icon] i,
                .${classToken}:hover [data-button-icon] svg {
                    color: ${states.hover.icon.color} !important;
                }

                .${classToken}:active {
                    background-color: ${states.active.background};
                    border-color: ${states.active.border};
                    color: ${states.active.color};
                }

                .${classToken}:active [data-button-icon] {
                    background-color: ${states.active.icon.background};
                    color: ${states.active.icon.color};
                }

                .${classToken}:active [data-button-icon] i,
                .${classToken}:active [data-button-icon] svg {
                    color: ${states.active.icon.color} !important;
                }

                .${classToken}:disabled {
                    background-color: ${states.disabled.background};
                    border-color: ${states.disabled.border};
                    color: ${states.disabled.color};
                    cursor: not-allowed;
                    opacity: 0.6;
                }

                .${classToken}:disabled [data-button-icon] {
                    background-color: ${states.disabled.icon.background};
                    color: ${states.disabled.icon.color};
                }

                .${classToken}:disabled [data-button-icon] i,
                .${classToken}:disabled [data-button-icon] svg {
                    color: ${states.disabled.icon.color} !important;
                }

                ${
                    hideLabelUnder
                        ? `
                    @media ${theme.breakpoints[hideLabelUnder].max} {
                        .${labelToken} { display: none; }
                    }
                `
                        : ''
                }

                ${
                    hideLabelOver
                        ? `
                    @media ${theme.breakpoints[hideLabelOver].min} {
                        .${labelToken} { display: none; }
                    }
                `
                        : ''
                }

                ${
                    appearance === 'secondary-outlined' || isNeutral
                        ? `
                    .${classToken} [data-button-icon] {
                        background: transparent !important;
                        color: inherit;
                    }
                    .${classToken} [data-button-icon] svg {
                        color: inherit !important;
                    }
                `
                        : ''
                }
                ${responsivePaddingCss}
                ${responsiveLabelCss}
                ${
                    appearance === 'gradient' && variation === 'filled'
                        ? `
                    .${classToken} [data-button-icon] {
                        background: transparent !important;
                    }
                    .${classToken}:hover:not(:disabled) { filter: brightness(0.95); }
                    .${classToken}:active:not(:disabled) { filter: brightness(0.9); }
                    .${classToken}:focus-visible {
                        outline: 2px solid ${theme.palette.color.primary.main};
                        outline-offset: 3px;
                    }
                `
                        : ''
                }
            `}</style>
    );

    return (
        <>
            {styleElement}
            <button
                disabled={disabled}
                id={id}
                onClick={onClick}
                type={type}
                form={form}
                className={cn(
                    classToken,
                    'relative inline-flex items-center whitespace-nowrap no-underline outline-none',
                )}
                style={buttonStyle}
            >
                {children && (
                    <span className={cn(labelToken, 'flex items-center')} style={labelStyle}>
                        {children}
                    </span>
                )}
                {icon && (
                    <ButtonIcon
                        position={iconPosition}
                        icon={loading ? <IconSpinner /> : icon}
                        size={size}
                        gap={iconGap}
                        variant={variant}
                        variation={variation}
                        hideLabelUnder={hideLabelUnder}
                        hideLabelOver={hideLabelOver}
                    />
                )}
                {!icon && loading && (
                    <ButtonIcon
                        position={iconPosition}
                        icon={<IconSpinner />}
                        gap="none"
                        size={size}
                        variant={variant}
                        variation={variation}
                        hideLabelUnder={hideLabelUnder}
                        hideLabelOver={hideLabelOver}
                    />
                )}
            </button>
        </>
    );
};
